import StaffNav from './managernav';
import { useEffect, useState } from 'react';
import { format, parseISO, isValid } from 'date-fns';
import { 
  UserIcon, 
  PencilIcon, 
  CheckIcon, 
  XMarkIcon,
  BriefcaseIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  CalendarDaysIcon
} from '@heroicons/react/20/solid';

export default function MoreInfo({ person }) {
  const [editField, setEditField] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const [reload, setReload] = useState(false);
  const [roles, setRoles] = useState([]);
  const [accessLevel, setAccessLevel] = useState(null);
  const [newAccessLevel, setNewAccessLevel] = useState({ id: person.id, access: person.Access });
  const [contractDetails, setContractDetails] = useState(null);
  const [contractType, setContractType] = useState('');
  const [contractHours, setContractHours] = useState('');
  const [contractDays, setContractDays] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Format date helper function
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      const date = parseISO(dateString);
      return isValid(date) ? format(date, 'dd/MM/yyyy') : dateString;
    } catch (error) {
      return dateString;
    }
  };

  // Show success message temporarily
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };
 

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        // Fetch staff data for the given person.id
        const staffResponse = await fetch(`https://api.insightos.co.uk/api/getstaff/${person.id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
  
        // Fetch roles data (requires authentication)
        const rolesResponse = await fetch("https://api.insightos.co.uk/api/allroles", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: 'include',

        });
  
        // Process roles response
        if (rolesResponse.ok) {
          const rolesData = await rolesResponse.json();
          console.log('Fetched Roles Data:', rolesData.data); 
          // Assuming rolesData.data is an array of role objects
          setRoles(rolesData.data || []);
        } else {
          console.error('Failed to fetch roles data');
        }
  
        // Process staff response
        if (staffResponse.ok) {
          const staffData = await staffResponse.json();
          console.log('Fetched Staff Data:', staffData.data); 
          // Set field values using the first object from the staff data array
          setFieldValues(staffData.data[0] || {});
        } else {
          console.error('Failed to fetch staff data');
        }
      } catch (error) {
        console.error('Error fetching staff data:', error);
      }
    };
  
    if (person?.id) {
      fetchStaff();
    }
  }, [person.id, reload]);
  


  
  // Debug: Log fieldValues to see what is being set
  useEffect(() => {
    console.log('Current fieldValues:', fieldValues);
  }, [fieldValues]);
  
  const handleEditClick = (field) => {
    setEditField(field);
    setFieldValues({ ...fieldValues, [field]: fieldValues[field] || '' });
  };

  const handleCancel = () => {
    setEditField(null);
  };

  const handleSave = async (field) => {
    setLoading(true);
    console.log('Field:', field, 'Value:', fieldValues[field]);
    
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/updatestaff/${person.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ field, value: fieldValues[field] }),
      });

      if (response.ok) {
        console.log('Field updated successfully');
        setEditField(null);
        setReload((prev) => !prev);
        showSuccess(`${field} updated successfully!`);
      } else {
        console.error('Failed to update field');
      }
    } catch (error) {
      console.error('Error saving field:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e, field) => {
    setFieldValues({ ...fieldValues, [field]: e.target.value });
  };

  const handleChangeAccessLevel = async (newAccessLevel) => {
    setLoading(true);
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/updateaccess/${newAccessLevel.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ access: newAccessLevel.access }),
      });

      if (response.ok) {
        console.log('Access level updated successfully');
        setAccessLevel(null);
        setReload((prev) => !prev);
        showSuccess('Access level updated successfully!');
        // Update local state immediately
        setFieldValues(prev => ({
          ...prev,
          access: newAccessLevel.access
        }));
      } else {
        console.error('Failed to update access level');
      }
    } catch (error) {
      console.error('Error updating access level:', error);
    } finally {
      setLoading(false);
    }
  };

  const addSmtpConfig = () => {
  setSmtpConfigs(prev => [...prev, createEmptySmtpConfig()]);
};

const removeSmtpConfig = (id) => {
  if (smtpConfigs.length <= 1) {
    setSmtpError('At least one SMTP configuration is required.');
    return;
  }
  setSmtpConfigs(prev => prev.filter(cfg => cfg.id !== id));
};

const updateSmtpConfig = (id, field, value) => {
  setSmtpConfigs(prev =>
    prev.map(cfg =>
      cfg.id === id ? { ...cfg, [field]: value } : cfg
    )
  );
};

// When provider changes, auto‑fill host/port/secure
const handleSmtpProviderChange = (id, providerKey) => {
  const preset = smtpProviderPresets[providerKey] || smtpProviderPresets.custom;
  setSmtpConfigs(prev =>
    prev.map(cfg =>
      cfg.id === id
        ? {
            ...cfg,
            provider: providerKey,
            host: preset.host,
            port: preset.port,
            secure: preset.secure,
          }
        : cfg
    )
  );
};

  const handleContractSave = async () => {
    setLoading(true);
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/updatecontract/${person.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ 
          contractDays,
          contractType,
          contractHours
         }),
      });

      if (response.ok) {
        console.log('Contract details updated successfully');
        setContractDetails(null);
        setReload((prev) => !prev);
        showSuccess('Contract details updated successfully!');
        // Update local state immediately
        setFieldValues(prev => ({
          ...prev,
          ContractType: contractType,
          ContractedDays: contractDays,
          ContractedHours: contractHours
        }));
      } else {
        console.error('Failed to update contract details');
      }
    } catch (error) {
      console.error('Error updating contract details:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderField = (label, field, isDate = false) => {
    const displayValue = isDate ? formatDate(fieldValues[field]) : (fieldValues[field] || 'Not set');
    
    return (
      <div key={field} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <DocumentTextIcon className="h-4 w-4 text-blue-600" />
            </div>
            <label className="text-sm font-semibold text-gray-900">{label}</label>
          </div>
          {editField !== field && (
            <button
              onClick={() => handleEditClick(field)}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors duration-200"
            >
              <PencilIcon className="h-3 w-3 mr-1" />
              Edit
            </button>
          )}
        </div>
        
        {editField === field ? (
          <div className="space-y-3">
            <input
              type={isDate ? "date" : "text"}
              value={fieldValues[field] || ''}
              onChange={(e) => handleChange(e, field)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              placeholder={`Enter ${label.toLowerCase()}`}
            />
            <div className="flex space-x-2">
              <button
                onClick={() => handleSave(field)}
                disabled={loading}
                data-component="employee-field-editor"
                data-time-saved="5"
                data-feature={`save_${field}_put`}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-200 disabled:opacity-50"
              >
                <CheckIcon className="h-4 w-4 mr-1" />
                {loading ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancel}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-200"
              >
                <XMarkIcon className="h-4 w-4 mr-1" />
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-900 font-medium bg-gray-50 rounded-lg px-4 py-3">
            {displayValue}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 rounded-xl shadow-lg focus:outline-none focus:ring-2 focus:ring-red-500 transition-all duration-200 mb-6"
          >
            <XMarkIcon className="h-4 w-4 mr-2" />
            Back
          </button>
          
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
                <UserIcon className="h-8 w-8 text-white" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Employee Profile</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Manage employee information, access levels, and contract details
            </p>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-center">
              <CheckIcon className="h-5 w-5 text-green-500 mr-3" />
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Role & Access Management */}
          <div className="lg:col-span-1 space-y-6">
            {/* Role Management */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
                <div className="flex items-center">
                  <BriefcaseIcon className="h-6 w-6 text-white mr-3" />
                  <h3 className="text-lg font-bold text-white">Role Management</h3>
                </div>
              </div>
              <div className="p-6">
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Current Role: <span className="font-normal text-blue-600">{fieldValues.Role || 'Not assigned'}</span>
                </label>
                <select
                  value={fieldValues.Role || ''}
                  onChange={(e) => handleChange(e, 'Role')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                >
                  <option value="" disabled>Select role</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.Name}>
                      {role.Name}
                    </option>
                  ))}
                </select>
                <div className="mt-4 flex space-x-2">
                  <button
                    className="flex-1 inline-flex justify-center items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-200"
                    onClick={() => handleSave('Role')}
                    disabled={loading}
                    data-component="employee-role-management"
                    data-feature="save_role_put"
                    data-time-saved="5"
                  >
                    <CheckIcon className="h-4 w-4 mr-1" />
                    {loading ? 'Saving...' : 'Save Role'}
                  </button>
                </div>
              </div>
            </div>

            {/* Access Level Management */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-green-600 to-blue-600 px-6 py-4">
                <div className="flex items-center">
                  <ShieldCheckIcon className="h-6 w-6 text-white mr-3" />
                  <h3 className="text-lg font-bold text-white">Access Control</h3>
                </div>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Current Access Level</label>
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {fieldValues.access || person.Access || 'Not set'}
                  </div>
                </div>
                
                {!accessLevel && (
                  <button
                    onClick={() => setAccessLevel(person)}
                    className="w-full inline-flex justify-center items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all duration-200"
                  >
                    <PencilIcon className="h-4 w-4 mr-1" />
                    Change Access Level
                  </button>
                )}
                
                {accessLevel && accessLevel.id === person.id && (
                  <div className="space-y-4">
                    <select
                      value={newAccessLevel.access}
                      onChange={(e) => setNewAccessLevel({ id: person.id, access: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    >
                      <option value="">Select access level</option>
                      <option value="staff">Staff</option>
                      <option value="manager">Manager</option>
                      <option value="admin">Admin/HR</option>
                    </select>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleChangeAccessLevel(newAccessLevel)}
                        disabled={loading}
                        data-component="employee-access-control"
                        data-time-saved="5"
                        data-feature="update_access_level_put"
                        className="flex-1 inline-flex justify-center items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all duration-200 disabled:opacity-50"
                      >
                        <CheckIcon className="h-4 w-4 mr-1" />
                        {loading ? 'Updating...' : 'Save'}
                      </button>
                      <button
                        onClick={() => setAccessLevel(null)}
                        className="flex-1 inline-flex justify-center items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-200"
                      >
                        <XMarkIcon className="h-4 w-4 mr-1" />
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Contract Details */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4">
                <div className="flex items-center">
                  <DocumentTextIcon className="h-6 w-6 text-white mr-3" />
                  <h3 className="text-lg font-bold text-white">Contract Details</h3>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Contract Type:</span>
                    <span className="text-sm font-medium text-gray-900">{fieldValues.ContractType || person.ContractType || 'Not set'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Contracted Days:</span>
                    <span className="text-sm font-medium text-gray-900">{fieldValues.ContractedDays || person.ContractedDays || 'Not set'}</span>
                  </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Contracted Hours:</span>
                      <span className="text-sm font-medium text-gray-900">{fieldValues.ContractedHours || person.ContractedHours || 'Not set'}</span>
                    </div>
                </div>
                
                {!contractDetails && (
                  <button
                    onClick={() => setContractDetails(person)}
                    className="w-full inline-flex justify-center items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-200"
                  >
                    <PencilIcon className="h-4 w-4 mr-1" />
                    Update Contract
                  </button>
                )}
                
                {contractDetails && contractDetails.id === person.id && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Contract Type</label>
                      <select
                        value={contractType}
                        onChange={(e) => setContractType(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      >
                        <option value="">Select contract type</option>
                        <option value="0 Hour">0 Hour</option>
                        <option value="Part Time">Part Time</option>
                        <option value="Full Time">Full Time</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Contracted Days</label>
                      <input
                        type="text"
                        value={contractDays}
                        onChange={(e) => setContractDays(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        placeholder="e.g., 5"
                      />
                    </div>
                    

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Contracted Hours</label>
                      <input
                        type="text"
                        value={contractHours}
                        onChange={(e) => setContractHours(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        placeholder="e.g., 40"
                      />
                    </div>
                                      
                    <div className="flex space-x-2">
                      <button
                        className="flex-1 inline-flex justify-center items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-200"
                        onClick={handleContractSave}
                        disabled={loading}
                        data-component="employee-contract-management"
                        data-time-saved="5"
                        data-feature="update_contract_put"
                      >
                        <CheckIcon className="h-4 w-4 mr-1" />
                        {loading ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        className="flex-1 inline-flex justify-center items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-200"
                        onClick={() => setContractDetails(null)}
                      >
                        <XMarkIcon className="h-4 w-4 mr-1" />
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Personal Information */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4">
                <div className="flex items-center">
                  <CalendarDaysIcon className="h-6 w-6 text-white mr-3" />
                  <h3 className="text-lg font-bold text-white">Personal Information</h3>
                </div>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderField('Employee Id', 'EmployeeID')}
                  {renderField('Full Name', 'Name')}
                  {renderField('Email', 'Email')}
                  {renderField('Telephone', 'Telephone')}
                  {renderField('Emergency Contact', 'EmergencyContact')}
                  {renderField('Age', 'Age')}
                  {renderField('Date of Birth', 'DOB', true)}
                  {renderField('Address', 'Address')}
                  {renderField('Department', 'Department')}
                  {renderField('Management Level', 'ManagementLevel')}
                  {renderField('Hourly Rate', 'HourlyRate')}
                  {renderField('Start Date', 'StartDate', true)}
                  {renderField('National Insurance Number', 'NationalInsuranceNumber')}
                  {renderField('Medical', 'Medical')}
                  
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
